import { useState } from 'react';
import { buttonClass, cn } from '@/core/ui';
import { ArrowSquareOut, Check, Copy } from '@/core/ui/icons';
import { androidIntentUrl, insideApp, type InAppBrowser } from '../domain/in-app-browser';

/**
 * Aviso no botão do Google/Apple quando a página está aberta dentro de um
 * app (Instagram, Facebook, WhatsApp…): o provedor recusa o login ali.
 *
 * No Android dá para sair do app direto para o Chrome (link `intent:`). No
 * iOS não existe esse atalho: o caminho é o menu do próprio app ("Abrir no
 * Safari"), então o aviso ensina e oferece copiar o link.
 */
export function OpenInBrowser({
  inApp,
  provider,
  id,
}: {
  inApp: InAppBrowser;
  /** "Google", "Apple". */
  provider: string;
  id?: string;
}) {
  const [copied, setCopied] = useState<'idle' | 'ok' | 'failed'>('idle');
  const href = window.location.href;
  const intent = inApp.os === 'android' ? androidIntentUrl(href) : null;

  async function copy() {
    try {
      await navigator.clipboard.writeText(href);
      setCopied('ok');
    } catch {
      setCopied('failed');
    }
  }

  return (
    <div
      id={id}
      role="status"
      className="space-y-3 rounded-2xl bg-surface-2 p-4 text-sm leading-relaxed text-fg-muted"
    >
      <div>
        <p className="font-medium text-fg">
          O login com {provider} não funciona {insideApp(inApp)}.
        </p>
        <p className="mt-1">
          Abra esta página no navegador do celular para entrar com {provider} — ou use seu e-mail aqui mesmo.
        </p>
      </div>

      {inApp.os === 'ios' && (
        <p>
          Toque no menu <strong className="font-semibold text-fg">•••</strong> no canto da tela e escolha{' '}
          <strong className="font-semibold text-fg">“Abrir no navegador”</strong> (ou “Abrir no Safari”).
        </p>
      )}
      {inApp.os === 'android' && (
        <p className="text-xs text-fg-subtle">
          Se nada acontecer, toque no menu <strong className="font-semibold">⋮</strong> e escolha “Abrir no
          Chrome” ou “Abrir no navegador”.
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        {intent && (
          <a href={intent} className={cn(buttonClass('primary', 'sm'), 'rounded-full')}>
            <ArrowSquareOut aria-hidden className="size-4" />
            Abrir no navegador
          </a>
        )}
        <button
          type="button"
          onClick={() => void copy()}
          className={cn(buttonClass(intent ? 'secondary' : 'primary', 'sm'), 'rounded-full')}
        >
          {copied === 'ok' ? (
            <Check aria-hidden className="size-4" />
          ) : (
            <Copy aria-hidden className="size-4" />
          )}
          {copied === 'ok' ? 'Link copiado' : 'Copiar link'}
        </button>
      </div>
      {copied === 'ok' && <p className="text-xs">Agora cole o link no Safari ou no Chrome.</p>}
      {copied === 'failed' && (
        <p className="text-xs">
          Não deu para copiar. Copie o endereço: <span className="break-all text-fg select-all">{href}</span>
        </p>
      )}
    </div>
  );
}
